/**
 * Determines the display mode of the context selector.
 */
export enum ContextSelectorType {
  /**
   * Renders context selector label as a dropdown.
   */
  Dropdown = 'dropdown',
  /**
   *  Renders context selector label as a button to open modal dialog.
   */
  Modal = 'modal',
}

/**
 * Represents a tab configuration for the context selector component.
 */
export interface Tab {
  /**
   * The label for the tab, displayed in the UI.
   */
  label: string;
  /**
   * The unique identifier for the tab.
   * - 'default': Renders tableInfo as single table in context body.
   * - 'tableColumnKey': Renders two tables in context body, first table is table with single column containing specified tableColumnKey.
   *  Second table is table with all columns except specified tableColumnKey.
   * - 'any-other-value': For any value other than 'default' or 'tableColumnKey', currently it renders tableInfo as single table in context body.
   * @default 'default'
   */
  value: string;
  /**
   * If provided, the context body will render two tables. First table will show table with single column containing specified column key,
   * and second table will show all columns except specified column key.
   * @default false
   */
  showGroupedTablesByValue?: boolean;
  /**
   * Optional flag indicating whether a separator should be displayed after this item.
   * @default 'false'
   */
  hasSeperator?: boolean;
  /**
   * Text for the search input in this tab.
   * It will be shown as the placeholder in the search box.
   */
  searchPlaceHolder: string;
}

/**
 * Represents the selected rows in a table.
 * The keys are the unique identifiers of the rows that are selected.
 * @example { 'row1': true, 'row2': true }
 */
export type SelectedRows = Record<string, true>;

/**
 * Represents a complex sort value for a table column.
 * This type is used when the column value is not a simple string or number, but rather
 * a more complex column where it needs to display some other value in the column,
 * while sorting based on a different value.
 * This is particularly useful for date values or other complex data types that require a specific sorting logic.
 * @example { value: 'Feb 2024', sortingValue: 1709141762000 }
 */
export type NonPrimitiveType = {
  /**
   * The value to be displayed in the column.
   */
  value: string | number;
  /**
   * The value used for sorting the column.
   */
  sortingValue: string | number | Date;
};

/**
 * Represents a single row in a table.
 * Each row is an object with string or number values, and must include a unique 'id' field.
 * Additional fields can be added as needed, and they will be displayed in the table.
 * @example { id: 'row1', primaryLabel: 'EN', secondaryLabel: 'Row 1', name: 'Row 1', age: 30, dateOfBirth: { value: 'Feb 2024', sortingValue: 1709141762000 } }
 */
export type TableRow = {
  /**
   * The key-value pairs representing the data in the row.
   * Each key is a string, and the value can be a string, number, or NonPrimitiveType.
   */
  [key: string]: string | number | NonPrimitiveType | undefined;
  /**
   * The unique identifier for the table row.
   */
  id: string;
  /**
   * Primary label to show on selection in the chip. It will be shown as bold text.
   * @default value of id field.
   */
  primaryLabel?: string;
  /**
   * Secondary label to show on selection in the chip. It will be shown as non-bold text.
   */
  secondaryLabel?: string;
};

/**
 * Describes the configuration for a single column in a data table or grid.
 */
export type ColumnConfiguration = {
  /**
   * The key in the data object that corresponds to this column.
   */
  columnKey: string;
  /**
   * The display name for the column. If not provided, columnKey will be used.
   */
  columnName: string;
  /**
   * Optional flag to enable sorting for this column.
   */
  isSortingEnabled?: boolean;
  /**
   * Optional flag to specify if this column is searchable.
   * If there are no searchable columns, all columns will be searched.
   * @default false
   */
  isSearchable?: boolean;
};

/**
 * Represents the structure of a table, including its data and column configuration.
 */
export type TableInfo = {
  /**
   * The data for the table, provided as an array of objects where each object represents a row.
   * Each object should have a unique identifier, i.e. 'id' field.
   */
  data: TableRow[];
  /**
   * The column configuration for the table.
   * Each object should define the key in the data object that corresponds respective column.
   */
  columnConfig: ColumnConfiguration[];
};

/**
 * Represents the configuration for column for which you can select only a single value, in the context selector.
 * When a value from this column is selected, a warning alert will be shown at the bottom of the context selector body.
 * This is useful for scenarios where selecting multiple values from a specific column could lead to issues or conflicts.
 */
export type RestrictedColumn = {
  /**
   * The key in the data object that corresponds to this column.
   */
  columnKey: string;
  /**
   * The text to display as warning alert at bottom of context selector body when column is selected.
   * NOTE: This text will be ignored if `restrictedValues` is set as when its set, the alert message
   * will be generated dynamically based on selected column value and columnKey.
   */
  alertMessage?: string;
  /**
   * If provided, the single value selection will be enforced only for the specified values of the column in this array.
   * If not provided, the single value selection will be enforced for all values of the column.
   */
  restrictedValues?: string[];
};

export type RestrictedColData = RestrictedColumn & {
  selectedValue?: string;
};
